/* eslint-disable */
import * as AuthSession from 'expo-auth-session'
import * as Updates from 'expo-updates'
import toQueryString from '@lib/utils/toQueryString'
import {
  getAuth0Options,
  LOGOUT_SESSION_CONFIG,
  AUTH0_AUTHORIZATION_REQUEST_URL,
  AUTH0_OAUTH_TOKEN_URL,
  AUTH0_USERINFO_URL,
} from '@lib/constants/auth0'
import jwtDecode from 'jwt-decode'
import axios from 'axios'
import gql from '@lib/gql'
import GET_USER_BY_EMAIL from '@lib/queries/getUserByEmail'
import env from '@lib/env'
import { useCurrentUser } from '@context/CurrentUserContext'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'
import { useMenu } from '@context/MenuContext'
import useSentry from '@hooks/useSentry'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { defaultProfileData } from '@screens/Profile/ProfileEditScreen'

interface Auth0Token {
  aud: string
  exp: number
  family_name: string
  given_name: string
  'https://hasura.io/jwt/claims': {
    'x-hasura-allowed-roles': string[]
    'x-hasura-default-role': string
    'x-hasura-user-id': string
  }
  iat: number
  iss: string
  locale: string
  name: string
  nickname: string
  nonce: string
  picture: string
  sub: string
  updated_at: string
}

interface Auth0UserInfo {
  data?: {
    email: string
  }
}

interface HasuraUserInfo {
  id?: string
  email?: string
  firstName?: string
  lastName?: string
  companyId?: string
  phone?: string
}

interface Auth0TokenData {
  auth0_user_id?: string
  jwtToken?: string
  accessToken?: string
  refreshToken?: string
  cancel?: boolean
}

const AUTH0_CLIENT_ID = env().AUTH0_CLIENT_ID

export default function () {
  const { currentUser, setCurrentUser, clearCurrentUser } = useCurrentUser()
  const { setSnackbarMessage } = useSnackbar()
  const { captureException } = useSentry()
  const { closeMenuWithoutAnimation } = useMenu()

  const logoutWithAuth0 = async () => {
    const result = await AuthSession.startAsync(LOGOUT_SESSION_CONFIG)
    if (result && result.type === 'success') {
      // Reset current user & profile data
      setCurrentUser({})
      await AsyncStorage.setItem('profileData', JSON.stringify(defaultProfileData))
      return true
    }
    return false
  }

  const loginWithAuth0 = async (): Promise<Auth0TokenData> => {
    /**
     * This is a complete implementation of the Authorization Code flow:
     * https://auth0.com/docs/flows/authorization-code-flow
     *
     * Useful info about parameters:
     * https://auth0.com/docs/api/authentication#social
     */

    // eslint-disable-next-line
    const { code_verifier, ...options } = await getAuth0Options()
    // @ts-ignore
    const authUrl = AUTH0_AUTHORIZATION_REQUEST_URL + toQueryString(options)

    const result = await AuthSession.startAsync({
      authUrl: authUrl,
    })

    if (!result) return { cancel: true }
    if (result.type === 'cancel') return { cancel: true }

    if (result.type === 'success') {
      const tokens = await axios.post(AUTH0_OAUTH_TOKEN_URL, {
        grant_type: 'authorization_code',
        client_id: AUTH0_CLIENT_ID,
        code_verifier: code_verifier,
        code: result.params.code,
        redirect_uri: options.redirect_uri,
        state: env().BUILD_TARGET,
      })

      const decoded = jwtDecode(tokens?.data.id_token) as Auth0Token
      const data = {
        auth0_user_id: decoded.sub,
        jwtToken: tokens?.data.id_token,
        refreshToken: tokens?.data.refresh_token,
        accessToken: tokens?.data.access_token,
      }

      return data
    }

    // In case of an error, we want to show a message to the user
    setSnackbarMessage('There was a problem with authentication', SnackbarThemes.error)
    return {}
  }

  const getUserInfo = async (accessToken: string, jwt: string): Promise<HasuraUserInfo> => {
    let auth0UserInfo: Auth0UserInfo

    try {
      auth0UserInfo = await axios.get(AUTH0_USERINFO_URL, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Login failed', SnackbarThemes.error)
      clearCurrentUser()
    }

    let result
    try {
      result = await gql.post('',
        {
          query: GET_USER_BY_EMAIL,
          variables: { email: auth0UserInfo!.data?.email },
        },
        {
          headers: { Authorization: `Bearer ${jwt}` },
        })
      if (result.data.errors) {
        throw new Error(result.data.errors[0])
      }
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Login failed', SnackbarThemes.error)
      clearCurrentUser()
    }

    /**
     * If for some reason the user exists in Auth0, but not in
     * Hasura, throw an error.
     *  */
    if (result?.data.data.users.length === 0) {
      throw new Error('User does not exist in Hasura')
    }

    /**
     * Build user info data
     */
    const userData = result?.data.data.users[0]

    return {
      id: userData?.id,
      email: userData?.email || "",
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      companyId: userData?.persona?.company_id || "",
      phone: userData?.phone || "",
    }
  }

  const handleLoginWithAuth0 = async () => {
    /**
     * Logs in with auth0 and sets current user info,
     * TODO: Maybe this try block isn't necessary?
     */
    let auth0UserInfo: Maybe<Auth0TokenData> = {}
    try {
      auth0UserInfo = await loginWithAuth0()

      // Login flow was cancelled
      if (auth0UserInfo.cancel) return
      if (!auth0UserInfo) throw new Error('Auth0 authentication failed')

      const userInfo = await getUserInfo(auth0UserInfo?.accessToken!, auth0UserInfo?.jwtToken!)

      setCurrentUser({
        ...currentUser,
        ...auth0UserInfo,
        ...userInfo,
      })
      closeMenuWithoutAnimation()
      if (userInfo?.firstName) setSnackbarMessage(`Welcome, ${userInfo.firstName}!`)
      else setSnackbarMessage('Successful login')
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Login failed', SnackbarThemes.error)
      setCurrentUser({})
    }
  }

  // Gets a Refreshed JWT based on the provided refreshToken
  const getRefreshedJwt = async (refreshToken: string) => {
    try {
      return await axios.post(
        AUTH0_OAUTH_TOKEN_URL,
        {
          grant_type: 'refresh_token',
          client_id: env().AUTH0_CLIENT_ID,
          refresh_token: refreshToken,
        },
        {
          headers: {
            'content-type': 'application/json',
          },
        },
      )
      } catch (error: any) {
        captureException(new Error(`refreshAccessToken Error: ${error.message}`))
      }
  }

  return { logoutWithAuth0, loginWithAuth0, getUserInfo, handleLoginWithAuth0, getRefreshedJwt }
}
